import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import Spinner from './Spinner';

export function Table({
  // New API (for Contrats page)
  headers,
  renderRow,
  // Old API (for Settings page)
  columns,
  // Common props
  data = [],
  loading = false,
  onRowClick,
  emptyMessage,
  className,
}) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 text-sm">
        {emptyMessage || t('common.noData')}
      </div>
    );
  }

  // Support both old (columns) and new (headers+renderRow) API
  const useOldAPI = columns && columns.length > 0;
  const useNewAPI = headers && headers.length > 0;

  if (!useOldAPI && !useNewAPI) {
    return (
      <div className="text-center py-12 text-gray-500 text-sm">
        Table configuration error
      </div>
    );
  }

  return (
    <div className={twMerge('w-full overflow-hidden', className)}>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {useOldAPI &&
                columns.map((col, idx) => (
                  <th
                    key={col.key || idx}
                    className={twMerge(
                      'px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider',
                      col.className
                    )}
                    style={col.width ? { width: col.width } : undefined}
                  >
                    {col.label}
                  </th>
                ))}
              {useNewAPI &&
                headers.map((header, idx) => (
                  <th
                    key={idx}
                    className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {header}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, rowIdx) => (
              <tr
                key={row.id || rowIdx}
                onClick={() => onRowClick?.(row)}
                className={clsx(
                  'transition-colors',
                  onRowClick && 'cursor-pointer hover:bg-gray-50'
                )}
              >
                {useOldAPI &&
                  columns.map((col, colIdx) => (
                    <td
                      key={col.key || colIdx}
                      className={twMerge(
                        'px-6 py-4 whitespace-nowrap text-sm text-gray-900',
                        col.cellClassName
                      )}
                    >
                      {col.render
                        ? col.render(row[col.key], row, rowIdx)
                        : row[col.key]}
                    </td>
                  ))}
                {useNewAPI && renderRow && renderRow(row, rowIdx)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Table;
